/**
 * Utility to calculate leftover items for direct trips (SUPPLIER→CUSTOMER)
 * Leftover = purchased qty - delivered qty per item name
 */
import { TripItem } from '../types/entities';

export interface LeftoverItem {
  itemId?: number;
  itemName: string;
  purchaseQty: number;
  saleQty: number;
  leftoverQty: number;
  unit: string;
}

/**
 * Returns list of items with leftover qty > 0
 * Groups by itemName, computes purchase vs sale difference
 */
export function calculateLeftoverItems(items: TripItem[]): LeftoverItem[] {
  const purchaseMap = new Map<string, { itemId?: number; qty: number; unit: string }>();
  const saleMap = new Map<string, number>();

  for (const item of items) {
    const role = item.itemRole ?? 'BOTH';

    if (role === 'PURCHASE' || role === 'BOTH') {
      const existing = purchaseMap.get(item.itemName);
      if (existing) {
        existing.qty += Number(item.volumeWeight);
      } else {
        purchaseMap.set(item.itemName, {
          itemId: item.itemId,
          qty: Number(item.volumeWeight),
          unit: item.unit,
        });
      }
    }

    if (role === 'SALE' || role === 'BOTH') {
      saleMap.set(item.itemName, (saleMap.get(item.itemName) ?? 0) + Number(item.volumeWeight));
    }
  }

  const leftovers: LeftoverItem[] = [];

  for (const [itemName, purchase] of Array.from(purchaseMap.entries())) {
    const saleQty = saleMap.get(itemName) ?? 0;
    const leftoverQty = purchase.qty - saleQty;
    if (leftoverQty > 0.001) {
      leftovers.push({
        itemId: purchase.itemId,
        itemName,
        purchaseQty: purchase.qty,
        saleQty,
        leftoverQty,
        unit: purchase.unit,
      });
    }
  }

  return leftovers;
}

/**
 * Check if a trip is a direct trip (SUPPLIER→CUSTOMER)
 */
export function isDirectTrip(trip: { pickupType?: string; deliveryType?: string }): boolean {
  return trip.pickupType === 'SUPPLIER' && trip.deliveryType === 'CUSTOMER';
}
